import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PortalLayout from "@/components/PortalLayout";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", phone: "", country: "", address: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        country: user.country || "",
        address: user.address || "",
      });
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error(t("forms.required"));
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch("/auth/profile", {
        name: form.name,
        phone: form.phone,
        country: form.country,
        address: form.address,
      });
      setUser(res.data);
      toast.success(t("portal.profileSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("portal.profileError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout active="profile">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-sm bg-secondary text-foreground"><User className="h-5 w-5" /></span>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-foreground">{t("portal.profile")}</h1>
            <p className="mt-1 text-muted-foreground">{t("portal.profileSubtitle")}</p>
          </div>
        </div>

        <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          onSubmit={save} className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8 card-soft space-y-5" data-testid="profile-form">
          <div>
            <Label className="text-sm text-muted-foreground">{t("forms.email")}</Label>
            <div className="mt-1.5 flex items-center gap-2 rounded-sm border border-border bg-secondary/50 px-3 h-11 text-sm text-foreground" data-testid="profile-email">
              <Mail className="h-4 w-4 text-muted-foreground" /> {user?.email}
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> {t("portal.verified")}</span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{t("portal.emailReadonly")}</p>
          </div>

          <div>
            <Label htmlFor="pf-name" className="text-sm text-muted-foreground">{t("forms.name")}</Label>
            <Input id="pf-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm h-11" placeholder={t("forms.namePlaceholder")} data-testid="profile-name" />
          </div>

          <div>
            <Label htmlFor="pf-phone" className="text-sm text-muted-foreground">{t("forms.phone")}</Label>
            <Input id="pf-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-sm h-11" placeholder="+1 555 123 4567" data-testid="profile-phone" />
          </div>

          <div>
            <Label htmlFor="pf-country" className="text-sm text-muted-foreground">{t("forms.country")}</Label>
            <Input id="pf-country" value={form.country} onChange={set("country")} className="mt-1.5 rounded-sm h-11" placeholder={t("forms.selectCountry")} data-testid="profile-country" />
          </div>

          <div>
            <Label htmlFor="pf-address" className="text-sm text-muted-foreground">{t("forms.address")}</Label>
            <Textarea id="pf-address" value={form.address} onChange={set("address")} className="mt-1.5 rounded-sm min-h-[88px]" placeholder="Street, city, postal code" data-testid="profile-address" />
          </div>

          <div className="flex justify-end pt-1">
            <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="profile-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
            </Button>
          </div>
        </motion.form>
      </div>
    </PortalLayout>
  );
}
